import { Stack, Text, mergeStyles } from "@fluentui/react";
import { FC, useEffect, useState } from "react";
import { DocumentStack } from "../../modules/PrecedentDocuments/DocumentStack";
import { FunctionalityButtonStack } from "../../modules/Functionality/FunctionalityButtonStack";
import { ProjectStack } from "../../modules/Projects/ProjectStack";
import FadeTransition from "../../components/FadeTransition";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import useModal from "../../hooks/useModal";
import { FormModal } from "../../components/modals/Modal";
import { colorPallete } from "../../assets/color";
import { VerticalStack } from "../../components/Stack";
import { deleteProjectReset } from "../../modules/redux/ProjectDeleteSlice";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";
import Help from "../../components/Help";
import DragAndDrop from "../../components/DragAndDrop/DragAndDrop";
import { ProjectDataButtonStack } from "../../modules/ProjectsData/ProjectDataButtonStack";
import { ProjectService } from "../../services/ProjectService";
import useIsSignedIn from "../../hooks/useIsSignedIn";

const Homepage: FC = () => {
  const { isModalOpen, showModal, hideModal } = useModal();
  const [deleteProjectSuccess, setDeleteProjectSuccess] = useState(false);
  const showDeleteProjectSuccessModal = () => setDeleteProjectSuccess(true);
  const hideDeleteProjectSuccessModal = () => setDeleteProjectSuccess(false);
  const [projectLimit, setProjectLimit] = useState<number>(0);
  const deleteProject = useAppSelector((state) => state.deleteProject.value);
  const isRedirectLinksLoading = useAppSelector((state) => state.redirectLinks.isLoading);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isSignedIn = useIsSignedIn();

  const fetchProjectsLimits = async () => {
    try {
      const limit: any = await ProjectService.getProjectLimit();
      setProjectLimit(limit.data[0].value);
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };
  
  useEffect(() => {
    if (isSignedIn) {
      fetchProjectsLimits();
    }
  }, [isSignedIn]);

  useEffect(() => {
    if (String(deleteProject).length !== 0) {
      showDeleteProjectSuccessModal();
      setTimeout(() => {
        dispatch(deleteProjectReset(""));
        hideDeleteProjectSuccessModal();
      }, 2000);
    }
  }, [deleteProject, dispatch]);

  const DeleteSuccessModal = () => {
    return (
      <FormModal
        title={"Success!"}
        TitleBackgroundColor={colorPallete.btGreen}
        isModalOpen={deleteProjectSuccess}
        showModal={showDeleteProjectSuccessModal}
        hideModal={hideDeleteProjectSuccessModal}
        width="480px"
        cancelButton
      >
        <VerticalStack style={{ height: "100px", padding: "20px 25px" }}>
          <Text
            style={{
              fontFamily: "Poppins",
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: "400",
              color: "var(--black, #323232)",
            }}
          >
            {t("project")} {deleteProject && deleteProject} {t("deleted")}
          </Text>
        </VerticalStack>
      </FormModal>
    );
  };

  return (
    <FadeTransition>
      <Stack
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          backgroundColor: "",
          position: "relative",
        }}
      >
        {isRedirectLinksLoading ? (
          <Stack style={styles.container}>
            <Loader />
            <Text style={styles.text}>{t("loading")}</Text>
          </Stack>
        ) : (
          <>
            <FunctionalityButtonStack />

            <Stack style={{ display: "flex", alignSelf: "flex-start" }}>
              <Text
                styles={{
                  root: {
                    fontSize: "18px",
                    color: colorPallete.black2,
                    paddingLeft: "15px",
                    lineHeight: "18px",
                    fontWeight: "400",
                    fontStyle: "normal",
                    marginTop: 10,
                  },
                }}
                className={mergeStyles({ textAlign: "start" })}
                style={{ fontFamily: "Poppins", textAlign: "start" }}
              >
                Pipeline
              </Text>
            </Stack>
            <ProjectDataButtonStack />

            <ProjectStack
              projectLimit={projectLimit}
              setProjectLimit={setProjectLimit}
            />

            <Stack
              styles={{
                root: {
                  padding: "0px 8px",
                  width: "100%",
                },
              }}
            >
              <DragAndDrop
                textStackDirection={"row"}
                isModalOpen={isModalOpen}
                showModal={showModal}
                hideModal={hideModal}
              />
            </Stack>
            <DocumentStack />
          </>
        )}
        <Help />
      </Stack>
      <DeleteSuccessModal />
    </FadeTransition>
  );
};

export default Homepage;

const styles = {
  container: {
    paddingTop: "70px",
  },
  text: {
    fontSize: "30px",
    fontFamily: "Poppins",
    paddingTop: "10px",
  },
};
